import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPlay,FiExternalLink,FiCalendar,FiMusic}=FiIcons;

function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
             style={{backgroundImage: 'url(https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&h=1080&fit=crop)',}}>
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.h1 
            className="text-6xl md:text-8xl lg:text-9xl font-bold bebas-neue gradient-text mb-6"
            initial={{opacity: 0, y: 50}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
          >
            BIG CITY SUPREME
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto"
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
          >
            Alternative Rock Legends from Edmonton, Alberta
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
          >
            <a 
              href="https://open.spotify.com/artist/1y4LSBAOXKcnWoCL0OgtHU" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 px-8 py-3 rounded-full font-semibold transition-colors glow-red"
            >
              <SafeIcon icon={FiPlay} className="h-5 w-5" />
              <span>Stream on Spotify</span>
            </a>
            <Link 
              to="/music" 
              className="flex items-center space-x-2 border border-yellow-500 hover:bg-yellow-500 hover:text-black px-8 py-3 rounded-full font-semibold transition-colors glow-yellow"
            >
              <SafeIcon icon={FiMusic} className="h-5 w-5" />
              <span>Our Music</span>
            </Link>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{y: [0, 10, 0]}}
          transition={{duration: 2, repeat: Infinity}}
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Featured Album Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{opacity: 0, x: -50}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <h2 className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-6">
                SUPER DELUXE
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Our debut album that proves we're not just another garage band from Edmonton. We're THE garage band from Edmonton.
              </p>
              <p className="text-gray-400 mb-8">
                Nine tracks of pure auditory gold that'll make your neighbors question their life choices. From the swagger of "Gold Dipped Diamonds" to the raw energy of "Razor Blade Boomerang" - it's everything you didn't know you needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://bigcitysupreme.bandcamp.com/album/super-deluxe" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-700 px-6 py-3 rounded-full font-semibold transition-colors text-black"
                >
                  <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
                  <span>Buy on Bandcamp</span>
                </a>
                <Link 
                  to="/music" 
                  className="flex items-center space-x-2 border border-red-500 hover:bg-red-500 px-6 py-3 rounded-full font-semibold transition-colors text-center"
                >
                  <span>View Tracklist</span>
                </Link>
              </div>
            </motion.div>
            <motion.div 
              className="relative"
              initial={{opacity: 0, x: 50}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <div className="aspect-square bg-gradient-to-br from-red-600 to-yellow-600 rounded-lg p-8 glow-red">
                <div className="w-full h-full bg-black rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl font-bold bebas-neune gradient-text mb-4">
                      SUPER
                    </div>
                    <div className="text-4xl font-bold bebas-neune text-white">
                      DELUXE
                    </div>
                    <div className="text-sm text-gray-400 mt-4">
                      BIG CITY SUPREME
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              {number: '2010', label: 'Formed'},
              {number: '4', label: 'Rock Gods'},
              {number: '9', label: 'Epic Tracks'},
              {number: '∞', label: 'Attitude'}
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                viewport={{once: true}}
              >
                <div className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 uppercase tracking-wide text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-red-900 to-yellow-900 noise-bg">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold bebas-neune text-white mb-6"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            Ready to Join the Supreme Experience?
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-200 mb-8"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            viewport={{once: true}}
          >
            Don't just listen to rock - live it, breathe it, become one with the Supreme sound.
          </motion.p>
          <motion.div 
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
            viewport={{once: true}}
          >
            <Link 
              to="/shows" 
              className="inline-flex items-center space-x-2 bg-white text-black hover:bg-gray-200 px-8 py-4 rounded-full font-bold text-lg transition-colors"
            >
              <SafeIcon icon={FiCalendar} className="h-6 w-6" />
              <span>See Us Live</span>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Home;